import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, onUnmounted } from 'vue';
import TextTruncatedChip from '@/components/TextTruncatedChip.vue';
import sharedMessages from '@/lib/shared-messages';
import { DepartmentTreeNode } from '@/interfaces/model/general/department';
import { collectSubtreeIds } from '@/utils/departmentTree';


export default /*#__PURE__*/_defineComponent({
  __name: 'DepartmentTreeSelect',
  props: {
    tree: null,
    selectedValues: null,
    label: null,
    placeholder: null,
    textFieldPlaceholder: null,
    noneOptionLabel: null,
    isNoneSelected: { type: Boolean },
    disabled: { type: Boolean },
    outlined: { type: Boolean },
    labelByUuid: null
  },
  emits: ["onChange", "updateIncludeNone"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'onChange', value: string[]): void;
  (e: 'updateIncludeNone', value: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  tree: DepartmentTreeNode[];
  selectedValues: string[];
  label?: string;
  placeholder?: string;
  textFieldPlaceholder?: string;
  noneOptionLabel?: string;
  isNoneSelected?: boolean;
  disabled?: boolean;
  /** Outlined box with a floating label (form dialogs), vs. the plain
   * underlined trigger with the label above it (filter panels). */
  outlined?: boolean;
  /**
   * Fallback uuid -> name/path map for chip labels, used when a selected
   * value isn't in `tree` (e.g. `tree` is scoped to the current user's
   * editable departments, but a value assigned earlier by someone else
   * falls outside that scope). Does not affect what's selectable.
   */
  labelByUuid?: Map<string, string>;
};





const menuOpen = ref(false);
const searchKeyword = ref('');

const chipAreaRef = ref<HTMLDivElement | null>(null);
const resizeObserver = ref<ResizeObserver | null>(null);
const truncateMaxLength = ref<number | undefined>(undefined);

const selectedUuids = computed(() => props.selectedValues);
const selectedSet = computed(() => new Set(selectedUuids.value));

/** uuid -> name, for the selected-chips area. */
const nameByUuid = computed(() => {
  const acc = new Map<string, string>();
  const walk = (nodes: DepartmentTreeNode[]) => {
    nodes.forEach((node) => {
      acc.set(node.uuid, node.name);
      walk(node.children);
    });
  };
  walk(props.tree);
  return acc;
});

/** Keeps a node when its own name matches, or any descendant's does - so a
 * match's ancestors stay visible to show where it sits in the hierarchy. */
const filterTree = (
  nodes: DepartmentTreeNode[],
  query: string
): DepartmentTreeNode[] => {
  if (!query) return nodes;
  const q = query.toLowerCase();
  return nodes.reduce<DepartmentTreeNode[]>((acc, node) => {
    const children = filterTree(node.children, query);
    if (node.name.toLowerCase().includes(q) || children.length > 0) {
      acc.push({ ...node, children });
    }
    return acc;
  }, []);
};

const filteredTree = computed(() =>
  filterTree(props.tree, searchKeyword.value)
);

/**
 * Every node is independently selectable, regardless of children: a
 * department can be assigned on its own even when it has child departments.
 * Checking a node cascades to its descendants; a node shows indeterminate
 * when only some of itself+descendants are selected.
 */
const checkState = (
  node: DepartmentTreeNode
): 'checked' | 'indeterminate' | 'unchecked' => {
  const ids = collectSubtreeIds(node);
  const selectedCount = ids.filter((id) => selectedSet.value.has(id)).length;
  if (selectedCount === 0) return 'unchecked';
  return selectedCount === ids.length ? 'checked' : 'indeterminate';
};

const toggleNode = (node: DepartmentTreeNode, checked: boolean) => {
  const ids = new Set(collectSubtreeIds(node));
  const next = checked
    ? [
        ...selectedUuids.value,
        ...Array.from(ids).filter((id) => !selectedSet.value.has(id)),
      ]
    : selectedUuids.value.filter((id) => !ids.has(id));
  emits('onChange', next);
};

const toggleNoneOption = () => {
  emits('updateIncludeNone', !props.isNoneSelected);
};

const removeSelected = (uuid: string) => {
  emits(
    'onChange',
    selectedUuids.value.filter((v) => v !== uuid)
  );
};

const clearAll = () => {
  emits('onChange', []);
  if (props.noneOptionLabel) emits('updateIncludeNone', false);
};

onMounted(() => {
  if (chipAreaRef.value) {
    resizeObserver.value = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        const coef = width < 250 ? 2 : 1;
        truncateMaxLength.value = Math.trunc(width / (15 * coef));
      }
    });
    resizeObserver.value.observe(chipAreaRef.value);
  }
});

onUnmounted(() => {
  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
  }
});

return { __sfc: true,props, emits, menuOpen, searchKeyword, chipAreaRef, resizeObserver, truncateMaxLength, selectedUuids, selectedSet, nameByUuid, filterTree, filteredTree, checkState, toggleNode, toggleNoneOption, removeSelected, clearAll, TextTruncatedChip, sharedMessages }
}

})